import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Sidebar from '../Dashboard/Sidebar';
import Loader from '../layouts/Loader';
import { getBill, updateBill } from '../../actions/billingActions';
import { clearBillUpdated, clearBillingError } from '../../slices/billingSlice';
import { getTreatments } from "../../actions/treatmentActions";
import { getAllStaff } from "../../actions/staffActions";
import { lookupPatientByPatientId } from "../../actions/patientActions";
import { downloadBillPDF } from '../../utils/billPdf';
import AppDatePicker from '../common/AppDatePicker';
import InvoicePreviewModal from './InvoicePreviewModal';

const currency = (n) =>
    `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const emptyItem = () => ({
    treatment: '',
    description: '',
    quantity: 1,
    unitPrice: 0,
});

export default function EditBill() {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { bill, loading, error, isBillUpdated } = useSelector((s) => s.billingState);
    const { treatments } = useSelector((state) => state.treatmentState);
    const { staffList } = useSelector((state) => state.staffState);

    useEffect(() => {
        dispatch(getTreatments());
        dispatch(getAllStaff());
    }, [dispatch]);

    /* ---- local form state ---- */
    const [items, setItems] = useState([emptyItem()]);
    const [discount, setDiscount] = useState(0);
    const [tax, setTax] = useState(0);
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState('Pending');
    const [doctor, setDoctor] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [invoiceDate, setInvoiceDate] = useState('');
    const [initialised, setInitialised] = useState(false);

    const [patientIdInput, setPatientIdInput] = useState('');
    const [lookingUp, setLookingUp] = useState(false);
    const [patientDetails, setPatientDetails] = useState(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    /* ---- load the bill once ---- */
    useEffect(() => {
        dispatch(getBill(id));
    }, [dispatch, id]);

    // Auto-select doctor if only 1 doctor exists
    useEffect(() => {
        const doctors = (staffList || []).filter(
            (s) => s.designation && s.designation.toLowerCase().includes("doctor")
        );
        if (!doctor && doctors.length === 1) {
            setDoctor(doctors[0].name);
        }
    }, [staffList, doctor]);

    /* ---- pre-fill form once the bill arrives ---- */
    useEffect(() => {
        if (bill && String(bill._id || bill.id) === String(id) && !initialised) {
            setItems(
                (bill.items || []).map((it) => ({
                    treatment: it.description || '',
                    description: it.description || '',
                    quantity: it.quantity ?? 1,
                    unitPrice: it.unitPrice ?? 0,
                }))
            );
            setDiscount(bill.discount ?? 0);
            setTax(bill.tax ?? 0);
            setNotes(bill.notes ?? '');
            setStatus(bill.status ?? 'Pending');
            setDoctor(bill.doctor ?? '');
            setPaymentMethod(bill.paymentMethod || 'Cash');
            setInvoiceDate(bill.createdAt ? new Date(bill.createdAt).toISOString().split('T')[0] : '');
            if (bill.patient) {
                setPatientDetails(bill.patient);
            }
            setInitialised(true);
        }
    }, [bill, id, initialised]);

    /* ---- toast feedback ---- */
    useEffect(() => {
        dispatch(clearBillUpdated());
        dispatch(clearBillingError());
        return () => {
            dispatch(clearBillUpdated());
            dispatch(clearBillingError());
        };
    }, [dispatch]);

    useEffect(() => {
        if (error) {
            toast(error, { type: 'error' });
            dispatch(clearBillingError());
        }
        if (isBillUpdated) {
            toast('Bill updated successfully!', { type: 'success' });
            dispatch(clearBillUpdated());
            const targetPatient = patientDetails?.patientId || patientDetails?._id || bill?.patient?.patientId || bill?.patient?._id;
            setTimeout(() => {
                if (targetPatient) {
                    navigate(`/patient/${targetPatient}`);
                } else {
                    navigate('/admin/billing');
                }
            }, 600);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, isBillUpdated, dispatch]);

    const handleLookup = async () => {
        let clean = patientIdInput.trim();
        if (!clean) {
            toast("Enter a patient ID or number first", { type: "error" });
            return;
        }
        try {
            setLookingUp(true);
            const patient = await lookupPatientByPatientId(clean);
            setPatientDetails(patient);
            toast(`Loaded details for ${patient.name}`, { type: "success" });
        } catch (err) {
            setPatientDetails(null);
            toast(err?.response?.data?.message || "Patient not found", { type: "error" });
        } finally {
            setLookingUp(false);
        }
    };

    const clearPatient = () => {
        setPatientDetails(null);
        setPatientIdInput("");
    };

    /* ---- item helpers ---- */
    const updateItem = (index, field, value) => {
        if (field === 'treatment' && value) {
            const isDuplicate = items.some(
                (other, oIdx) => oIdx !== index && (
                    (other.treatment && other.treatment.trim().toLowerCase() === value.trim().toLowerCase()) ||
                    (other.description && other.description.trim().toLowerCase() === value.trim().toLowerCase())
                )
            );
            if (isDuplicate) {
                toast(`"${value}" is already added to this bill`, { type: 'warning' });
                return;
            }
        }

        setItems((prev) =>
            prev.map((it, i) => {
                if (i !== index) return it;
                const updated = { ...it, [field]: value };
                if (field === 'treatment') {
                    const match = treatments?.find((t) => t.name === value);
                    updated.description = value;
                    if (match) updated.unitPrice = match.price;
                }
                return updated;
            })
        );
    };

    const addItemRow = () => setItems((prev) => [...prev, emptyItem()]);
    const removeItemRow = (index) => setItems((prev) => prev.filter((_, i) => i !== index));

    const subtotal = items.reduce(
        (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0),
        0
    );
    const estimatedTotal = subtotal - (Number(discount) || 0) + (Number(tax) || 0);

    /* ---- submit ---- */
    const submitEdit = (e) => {
        e.preventDefault();

        const cleanItems = items
            .filter((it) => it.description && it.unitPrice !== '')
            .map((it) => ({
                description: it.description,
                quantity: Number(it.quantity) || 1,
                unitPrice: Number(it.unitPrice),
            }));

        if (cleanItems.length === 0) {
            toast('Add at least one treatment / item', { type: 'error' });
            return;
        }

        if (!doctor) {
            toast('Please select a consulting doctor', { type: 'error' });
            return;
        }

        if (!patientDetails?._id) {
            toast('Please select a valid patient', { type: 'error' });
            return;
        }

        dispatch(
            updateBill(id, {
                patient: patientDetails._id,
                items: cleanItems,
                discount: Number(discount) || 0,
                tax: Number(tax) || 0,
                notes,
                doctor,
                paymentMethod,
                status,
                date: invoiceDate
            })
        );
    };

    if (loading && !initialised) return (
        <div className="row g-0">
      <div className="col-12 col-md-2"><Sidebar /></div>
      <div className="col-12 col-md-10 main-content">
            <Loader /></div>
        </div>
    );

    const patient = bill?.patient || {};

    return (
        <div className="row g-0">
      <div className="col-12 col-md-2"><Sidebar /></div>
      <div className="col-12 col-md-10 main-content">

                {/* Page header */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                    <div>
                        <h1 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>
                            Edit Bill
                            {bill?.invoiceNumber && (
                                <span className="ms-2" style={{
                                    fontSize: '0.85rem', fontWeight: 500,
                                    color: 'var(--primary)',
                                    background: 'rgba(13,148,136,0.1)',
                                    border: '1px solid rgba(13,148,136,0.25)',
                                    padding: '0.2em 0.7em', borderRadius: 8
                                }}>
                                    #{bill.invoiceNumber}
                                </span>
                            )}
                        </h1>
                        <p className="mb-0 mt-1" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                            Modify treatments, amounts, or payment status
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-sm"
                            style={{
                                background: 'rgba(100,116,139,0.08)',
                                color: '#475569',
                                border: '1px solid rgba(100,116,139,0.2)',
                                borderRadius: 8
                            }}
                            onClick={() => navigate('/admin/billing')}
                        >
                            <i className="fa fa-arrow-left me-1"></i> Back to Billing
                        </button>
                        {bill?._id && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-sm me-2"
                                    style={{
                                        background: 'rgba(59,130,246,0.1)',
                                        color: '#3b82f6',
                                        border: '1px solid rgba(59,130,246,0.25)',
                                        borderRadius: 8
                                    }}
                                    onClick={() => setShowPreviewModal(true)}
                                    title="View Invoice On Screen (Without Download)"
                                >
                                    <i className="fa fa-eye me-1"></i> View
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm me-2"
                                    style={{
                                        background: 'rgba(13,148,136,0.08)',
                                        color: 'var(--primary)',
                                        border: '1px solid rgba(13,148,136,0.25)',
                                        borderRadius: 8
                                    }}
                                    onClick={() => downloadBillPDF(bill, patient, 'computer')}
                                    title="Download Invoice PDF"
                                >
                                    <i className="fa fa-file-invoice me-1"></i> Invoice PDF
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    style={{
                                        background: 'rgba(59,130,246,0.08)',
                                        color: '#3b82f6',
                                        border: '1px solid rgba(59,130,246,0.25)',
                                        borderRadius: 8
                                    }}
                                    onClick={() => downloadBillPDF(bill, patient, 'signature')}
                                    title="Download PDF for Physical Signature"
                                >
                                    <i className="fa fa-file-signature me-1"></i> Signature PDF
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <form onSubmit={submitEdit}>

                    {/* ---------- Patient lookup / details ---------- */}
                    <div className="card mb-3">
                        <div className="card-body">
                            <h6 className="fw-bold mb-3" style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Patient
                            </h6>

                            {!patientDetails ? (
                                <div className="row g-2 align-items-end mb-2">
                                    <div className="col-12 col-sm-6 col-md-5">
                                        <label className="form-label small mb-1 fw-semibold">Patient ID</label>
                                        <div className="input-group">
                                            <span className="input-group-text fw-bold bg-light text-muted">GN-</span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="e.g. 5 or 0005"
                                                value={patientIdInput}
                                                onChange={(e) => {
                                                    let val = e.target.value;
                                                    // Strip leading GN- if pasted or typed
                                                    val = val.replace(/^(GN[\s\-_]*)?/i, '');
                                                    setPatientIdInput(val);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        handleLookup();
                                                    }
                                                }}
                                                autoFocus
                                            />
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            Enter number only (e.g. 5) & press Enter
                                        </small>
                                    </div>
                                    <div className="col-12 col-sm-4 col-md-3">
                                        <button
                                            type="button"
                                            className="btn btn-secondary w-100"
                                            onClick={handleLookup}
                                            disabled={lookingUp}
                                        >
                                            {lookingUp ? "Looking up..." : "Find Patient"}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <Fragment>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <span className="badge bg-success">
                                            Patient details fetched automatically for {patientDetails.patientId}
                                        </span>
                                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearPatient}>
                                            Change patient
                                        </button>
                                    </div>
                                    <div className="row g-2">
                                        <div className="col-md-4">
                                            <div className="form-label" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Name</div>
                                            <div className="fw-semibold">{patientDetails.name || '—'}</div>
                                        </div>
                                        <div className="col-md-2">
                                            <div className="form-label" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Patient ID</div>
                                            <div>{patientDetails.patientId || '—'}</div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-label" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Phone</div>
                                            <div>{patientDetails.phone || '—'}</div>
                                        </div>
                                        <div className="col-md-3">
                                            <div className="form-label" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Address</div>
                                            <div>{patientDetails.address || '—'}</div>
                                        </div>
                                    </div>
                                </Fragment>
                            )}
                        </div>
                    </div>

                    {/* ---------- Invoice & Doctor Info ---------- */}
                    <div className="card mb-3">
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <h6 className="fw-bold mb-2" style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Invoice Date
                                    </h6>
                                    <AppDatePicker
                                        label="Invoice Date"
                                        value={invoiceDate}
                                        onChange={(val) => setInvoiceDate(val)}
                                    />
                                </div>
                                <div className="col-md-4">
                                    <h6 className="fw-bold mb-2" style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Consulting Doctor
                                    </h6>
                                    <select
                                        className="form-control"
                                        value={doctor}
                                        onChange={(e) => setDoctor(e.target.value)}
                                    >
                                        <option value="">-- Select Doctor --</option>
                                        {staffList
                                            ?.filter(s => s.designation && s.designation.toLowerCase().includes('doctor'))
                                            .map(s => (
                                                <option key={s._id} value={s.name}>{s.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <h6 className="fw-bold mb-2" style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Payment Method
                                    </h6>
                                    <select
                                        className="form-control"
                                        value={paymentMethod}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                    >
                                        <option value="Cash">Cash</option>
                                        <option value="Card">Card</option>
                                        <option value="UPI">UPI</option>
                                        <option value="Bank Transfer">Bank Transfer</option>
                                        <option value="Insurance">Insurance</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ---- Status ---- */}
                    <div className="card mb-3">
                        <div className="card-body">
                            <h6 className="fw-bold mb-3" style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Bill Status
                            </h6>
                            <div className="d-flex flex-wrap gap-2">
                                {['Pending', 'Partially Paid', 'Paid', 'Cancelled'].map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => setStatus(s)}
                                        className="btn btn-sm"
                                        style={{
                                            borderRadius: 20,
                                            fontWeight: 600,
                                            fontSize: '0.8rem',
                                            padding: '0.3rem 0.9rem',
                                            ...(status === s
                                                ? {
                                                    background: s === 'Paid' ? 'var(--success)' :
                                                        s === 'Cancelled' ? 'var(--danger)' :
                                                            s === 'Partially Paid' ? 'var(--warning)' : '#64748b',
                                                    color: '#fff',
                                                    border: 'none',
                                                }
                                                : {
                                                    background: 'var(--bg-light)',
                                                    color: 'var(--text-muted)',
                                                    border: '1.5px solid var(--border)',
                                                }),
                                        }}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ---- Treatments ---- */}
                    <div className="card mb-3">
                        <div className="card-body">
                            <h6 className="fw-bold mb-3" style={{ color: 'var(--text-muted)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Treatments / Charges
                            </h6>

                            {/* Header row */}
                            <div className="row g-2 mb-1 d-none d-md-flex">
                                <div className="col-md-3"><span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Treatment</span></div>
                                <div className="col-md-3"><span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Description</span></div>
                                <div className="col-md-1"><span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Qty</span></div>
                                <div className="col-md-2"><span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Unit Price</span></div>
                                <div className="col-md-2 text-end"><span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Amount</span></div>
                            </div>

                            {items.map((it, index) => (
                                <div
                                    className="row g-2 align-items-center mb-2 py-2"
                                    key={index}
                                    style={{
                                        background: 'var(--bg-light)',
                                        borderRadius: 8,
                                        border: '1px solid var(--border)',
                                    }}
                                >
                                    <div className="col-12 col-md-3">
                                        <select
                                            className="form-select form-select-sm"
                                            value={it.treatment}
                                            onChange={(e) => updateItem(index, 'treatment', e.target.value)}
                                        >
                                            <option value="">Select Treatment</option>
                                            {treatments?.map((t) => {
                                                const isAlreadySelected = items.some(
                                                    (other, oIdx) => oIdx !== index && (
                                                        (other.treatment && other.treatment.trim().toLowerCase() === t.name.trim().toLowerCase()) ||
                                                        (other.description && other.description.trim().toLowerCase() === t.name.trim().toLowerCase())
                                                    )
                                                );
                                                return (
                                                    <option key={t.name} value={t.name} disabled={isAlreadySelected}>
                                                        {t.name}{isAlreadySelected ? " (Already added)" : ""}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                    <div className="col-12 col-md-3">
                                        <input
                                            className="form-control form-control-sm"
                                            placeholder="Description"
                                            value={it.description}
                                            onChange={(e) => updateItem(index, 'description', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-4 col-md-1">
                                        <input
                                            type="number" min="1"
                                            className="form-control form-control-sm"
                                            placeholder="Qty"
                                            value={it.quantity}
                                            onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-4 col-md-2">
                                        <input
                                            type="number" min="0" step="0.01"
                                            className="form-control form-control-sm"
                                            placeholder="Price"
                                            value={it.unitPrice}
                                            onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                                        />
                                    </div>
                                    <div className="col-4 col-md-2 text-end fw-semibold" style={{ fontSize: '0.875rem' }}>
                                        {currency((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0))}
                                    </div>
                                    <div className="col-12 col-md-1 text-end">
                                        <button
                                            type="button"
                                            className="btn btn-sm w-100"
                                            style={{
                                                background: 'rgba(239,68,68,0.08)',
                                                color: 'var(--danger)',
                                                border: '1px solid rgba(239,68,68,0.2)',
                                                borderRadius: 6,
                                            }}
                                            onClick={() => removeItemRow(index)}
                                            disabled={items.length === 1}
                                        >
                                            <i className="fa fa-times"></i>
                                        </button>
                                    </div>
                                </div>
                            ))}

                            <button
                                type="button"
                                className="btn btn-sm mt-2"
                                style={{
                                    background: 'rgba(13,148,136,0.08)',
                                    color: 'var(--primary)',
                                    border: '1px dashed rgba(13,148,136,0.4)',
                                    borderRadius: 8,
                                }}
                                onClick={addItemRow}
                            >
                                <i className="fa fa-plus me-1"></i> Add treatment
                            </button>
                        </div>
                    </div>

                    {/* ---- Discount / Tax / Notes / Totals ---- */}
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-sm-4">
                                    <label className="form-label">Discount (₹)</label>
                                    <input
                                        type="number" min="0" step="0.01"
                                        className="form-control"
                                        value={discount}
                                        onChange={(e) => setDiscount(e.target.value)}
                                    />
                                </div>
                                <div className="col-sm-4">
                                    <label className="form-label">Tax (₹)</label>
                                    <input
                                        type="number" min="0" step="0.01"
                                        className="form-control"
                                        value={tax}
                                        onChange={(e) => setTax(e.target.value)}
                                    />
                                </div>
                                <div className="col-sm-4">
                                    <label className="form-label">Notes</label>
                                    <input
                                        className="form-control"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                    />
                                </div>
                            </div>

                            {/* Totals summary */}
                            <div
                                className="d-flex flex-column align-items-end mt-4 gap-1"
                                style={{ fontSize: '0.875rem' }}
                            >
                                <div style={{ color: 'var(--text-muted)' }}>
                                    Subtotal: <span className="fw-semibold">{currency(subtotal)}</span>
                                </div>
                                {Number(discount) > 0 && (
                                    <div style={{ color: 'var(--success)' }}>
                                        Discount: <span className="fw-semibold">− {currency(Number(discount))}</span>
                                    </div>
                                )}
                                {Number(tax) > 0 && (
                                    <div style={{ color: 'var(--text-muted)' }}>
                                        Tax: <span className="fw-semibold">+ {currency(Number(tax))}</span>
                                    </div>
                                )}
                                <div
                                    className="fw-bold"
                                    style={{ fontSize: '1.25rem', color: 'var(--text-dark)', borderTop: '1px solid var(--border)', paddingTop: '0.5rem', minWidth: 200, textAlign: 'right' }}
                                >
                                    Total: {currency(estimatedTotal)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ---- Submit ---- */}
                    <div className="d-flex gap-2">
                        <button
                            className="btn btn-primary"
                            type="submit"
                            disabled={loading}
                        >
                            {loading
                                ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
                                : <><i className="fa fa-save me-2"></i>Save Changes</>
                            }
                        </button>
                        <button
                            type="button"
                            className="btn"
                            style={{
                                border: '1.5px solid var(--border)',
                                color: 'var(--text-muted)',
                                background: 'var(--bg-light)',
                                borderRadius: 8,
                            }}
                            onClick={() => navigate('/admin/billing')}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            <InvoicePreviewModal
                bill={bill}
                patient={patientDetails}
                isOpen={showPreviewModal}
                onClose={() => setShowPreviewModal(false)}
            />
        </div>
        </div>
    );
}




